import {flow, getEnv, getType, onSnapshot, types} from "mobx-state-tree";
import Group from "../../../models/Group";
import CodeType from "../../../models/CodeType";
import {TagSelectState} from "../../elements/tags/TagSelectState";
import {GroupSelectState} from "../../elements/groups/GroupSelectState";
import {CodeTypeSelectState} from "../../elements/codeTypes/CodeTypeSelectState";
import {PublishDomainSelectState} from "../../elements/publishDomain/PublishDomainSelectState";
import {ModalState} from "../../elements/modal/GenericModal";
import {StatusState} from "../../configs/status/StatusState";
import {PageTemplateSelectState} from "../../elements/pageTemplate/PageTemplateSelectState";
import {UtmModalState} from "../../utms/modal/UtmModalState";
import {RuleSetModalState} from "../../configs/rulesets/modal/RuleSetModalState";
import {Utm} from "../../../models/utms/UtmModel";
import {DesignerModalState} from "../../qr/modal/DesignerModalState";
import QrConfiguration from "../../../models/QrConfiguration";
import {DimensionSelectViewState} from "../../elements/dimensions/dimensionSelectViewState";
import lodash from 'lodash'
import {codeBehavior, codeTypes, Roles, uriPatternConstants} from "../../../utils/constants";
import Notification from "../../../utils/Notification";
import {validateAndFormatUrl, addTimestampToFileName, getFileFormat, getFileCategory, getValidProtocol} from "../../../utils/common";
import {ParameterSetModalState} from "../../configs/parametersets/modal/ParameterSetModalState";
import Parameters from "../../../models/Parameters";
import {walletExperienceDataBuilder} from "../../elements/walletPass/utils";
import {MediaFile} from "../../../models/mediaFiles/MediaFileModel";
import {MediaFileSelectState} from "../../elements/mediaFiles/MediaFileSelectState";
import {ViewFilePicState} from "../../mediaFiles/view/ViewFilePicState";
import {ExtendedDataSettingModalState} from "../../extendedData/modal/ExtendedDataSettingModalState";
import {updateInfoAfterSelectGroup} from "../../../utils/group";
import {DefaultSettingModel} from "../../../models/defaultSetting/DefaultSettingModel";
import {createCustomAjvValidator} from "../../../services/AjvValidator";
import { addUtmToCode } from "../../utms/utils/UtmUtils";
import DesignerState from "../../../components/qr/designer/DesignerState";
import { fetchAndHydrateDefaultSetting as fetchAndHydrateDefaultSettingUtil, buildDefaultSettingQuery } from "../../../utils/defaultSettingUtils";
import {protocolConstants} from "../../../utils/constants";

const defaultSchemaJson = {
  "type": "object",
  "properties": {
    "url": {
      "title": "Landing Url:",
      "type": "string",
      "validationMessage": {
        "default": "Url must be of proper format: http(s)://...",
        "302": "Url is required"
      }
    }
  },
  "required": ["url"]
};

const CreateCode = types.model('CreateCode', {
  isLoading: types.optional(types.boolean, false),
  name: types.optional(types.string, ""),
  keyword: types.optional(types.string, ""),

  tagSelectStore: types.maybe(types.late(() => types.reference(TagSelectState))),
  groupSelectStore: types.maybe(types.late(() => types.reference(GroupSelectState))),
  dimensionSelectViewState: types.maybe(types.late(() => types.reference(DimensionSelectViewState))),
  codeTypeSelectStore: types.maybe(types.late(() => types.reference(CodeTypeSelectState))),
  secondaryCodeTypeSelectStore: types.maybe(types.late(() => types.reference(CodeTypeSelectState))),
  publishDomainSelectStore: types.maybe(types.late(() => types.reference(PublishDomainSelectState))),
  pageTemplateSelectStore: types.maybe(types.late(() => types.reference(PageTemplateSelectState))),
  // vEventSelectStore: types.maybe(types.late(() => types.reference(VEventSelectState))),
  selectedGroup: types.maybe(types.reference(Group)),
  experienceSelected: types.maybe(types.reference(CodeType)),
  secondaryExperienceSelected: types.maybe(types.reference(CodeType)),
  isExperienceDynamic: types.optional(types.boolean, true),
  behavior: types.optional(types.string, ''),

  //qrUrl: types.optional(types.safeReference(QrUrl))    // domain | prefix | short  [placeholder for the final QRUrl, or the actual one]
  isAutoGeneratedUrl: types.optional(types.boolean, true),
  keywordStatus: types.maybe(types.boolean), // true - success, false - failure, undefined - warning
  selectedDimensions: types.optional(types.string, '{}'),
  selectedTags: types.optional(types.array(types.string), []),
  //types.optional(types.array(types.reference(Tag, {acceptsUndefined: true})), []),
  experienceData: types.optional(types.string, '{}'),
  secondaryExperienceData: types.optional(types.string, '{}'),
  extendedDataSettingModalState: types.maybe(types.late(() => ExtendedDataSettingModalState)),
  vCardDownLoadLinkData: types.optional(types.string, ''),
  experienceUISchema: types.optional(types.string, '{}'),
  secondaryExperienceUISchema: types.optional(types.string, '{}'),

  formHasErrors: types.optional(types.boolean, false),
  errorMessage: types.optional(types.string, "Null"),
  selectedDefaultUrl: types.optional(types.string, ''),
  inSubmission: types.optional(types.boolean, false),
  liveValidateSchema: types.optional(types.boolean, false),

  experienceReadonly: types.optional(types.boolean, false),
  qrUrlReadonly: types.optional(types.boolean, false),

  modalStore: types.maybe(ModalState),
  statusViewState: types.maybe(types.late(() => StatusState)),
  utmModalState: types.maybe(types.late(() => UtmModalState)),
  utm: types.maybe(types.late(() => Utm)),
  ruleSetModalState: types.maybe(types.late(() => RuleSetModalState)),
  rules: types.optional(types.string, "[]"),
  designerModalState: types.maybe(types.late(() => DesignerModalState)),
  existedDesignerState: types.maybe(types.late(() => DesignerState)),
  parameterSetModalState: types.maybe(types.late(() => ParameterSetModalState)),
  parameters: types.maybe(Parameters),
  imageUuid: types.optional(types.string, ""),
  previousPlatform: types.optional(types.string, ""),
  status: types.optional(types.string, "PUBLISHED"),
  defaultSetting :types.maybe(types.late(() => DefaultSettingModel)),
  defaultSettingFeatureFlag: types.optional(types.boolean, false),
  mediaFileSelectState: types.maybeNull(types.late(() => types.reference(MediaFileSelectState))),
  mediaFile: types.maybe(MediaFile),
  mediaFiles: types.optional(types.frozen(), {}),
  fileForS3Upload: types.optional(types.frozen(),{}),
  viewFilePicState: types.maybeNull(ViewFilePicState),
  isStaticBehaviorEnabled: types.optional(types.boolean, false),
  // singleUseFile: types.optional(types.boolean, true),
  shortUriPattern: types.optional(types.string, uriPatternConstants.DEFAULT_SHORT_URI_PATTERN),
  selectedProtocol: types.optional(types.enumeration([protocolConstants.HTTP, protocolConstants.HTTPS]), protocolConstants.HTTP),
  protocolDefaultsEnabled: types.optional(types.boolean, false),
  protocolDefaults: types.optional(types.frozen(), {}),

}).volatile((self) => ({
  modalComponent: types.object,
  modalComponentStore: types.object,
  modalSize: types.str,
  modalClassName: types.str,
  urlListener: types.fn,
  vCardDownloadLink: types.str,
  extendedDataUpdateDisposers: [],
  cancelUploads: types.boolean,
})).actions(self => ({
  setSelectedProtocol(protocol) {
    self.selectedProtocol = getValidProtocol(protocol);
  },
  handleNameChange(e) {
    self.name = e.target.value
  },
  handleKeywordChange(e) {
    self.keyword = e.target.value;
    self.debounceSetKeywordStatus();
  },
  setKeyword(keyword) {
    self.keyword = keyword;
    self.setKeywordStatus(self.keyword);
  },
  setStatus(status) {
    self.status = status
  },
  handleImageUuidChange(uuid) {
    self.imageUuid = uuid
  },
  handleHasErrors(boolean) {
    self.formHasErrors = boolean
  },
  handleErrorMessage(message) {
    self.errorMessage = message
  },
  setShortUrlListener(listener) {
    self.urlListener = listener
  },
  notifyShortUrl(short) {
    self.urlListener(short)
  },
  handleConfigModalPopUp(component, title, componentStore, size = "", className = "") {
    self.modalComponentStore = self[componentStore];
    self.modalComponent = component;
    self.modalSize = size;
    self.modalClassName = className;
    self.modalStore.toggle()
    self.modalStore.handleTitle(title)
  },
  setOnSelectChanges() {
    self.groupSelectStore?.setConsumer(self.selectGroup)
    self.tagSelectStore?.setConsumer(self.selectTag)
    self.codeTypeSelectStore?.setConsumer(self.selectExperience)
    self.secondaryCodeTypeSelectStore?.setConsumer(self.selectSecondaryExperience)
    self.publishDomainSelectStore?.setConsumer(self.selectPublishDomain)
    self.designerModalState?.setConsumer(self.handleImageUuidChange)
    self.dimensionSelectViewState?.setConsumer(self.selectDimension)
    self.publishDomainSelectStore?.selectedPublishDomain?.setConsumer(self.selectPrefix);
    self.mediaFileSelectState?.setConsumer(self.setMediaFileToSchema)
    // self?.extendedDataSettingModalState?.setConsumer(self.selectExtendedDataSelection);
  },
  setFilterOnExtendedDataStore(groupUUID) {
    self.extendedDataSettingModalState?.extendedDataSetSelectStore?.store?.setFilterOn((item) => {
      return (item?.owner?.groupUuid === groupUUID || item?.isExclusivelyGroupOwned === false);
    });

    self.extendedDataSettingModalState?.extendedDataSelectStore?.store?.setFilterOn((item) => {
      return (item?.owner?.groupUuid === groupUUID || item?.isExclusivelyGroupOwned === false);
    });
  },
  selectExtendedDataSelection(d) {
    const selectedExtendedData = self.extendedDataSettingModalState?.getSelectedExtendedDataMap() || {};
    self.extendedDataList = Object.values(selectedExtendedData);
  },
  setDefaultStatusBasedOnRoles(userRoles) {
    if (userRoles.includes(Roles.ALL) || userRoles.includes(Roles.PUBLISH)|| userRoles.includes(Roles.BATCH)) {
      self.status = "PUBLISHED";
    } else {
      self.status = "DRAFT";
    }
  },
  selectDimension(obj, parentUuid) {
    let existingDimension = JSON.parse(self?.selectedDimensions)
    existingDimension[parentUuid] = obj?.uuid
    existingDimension = lodash.pickBy(existingDimension, value => !(lodash.isEmpty(value))); // to delete empty  object
    self.selectedDimensions = JSON.stringify(existingDimension)
    self.fetchAndHydrateDefaultSetting()
  },
  selectPrefix(prefix) {
    if (self.keyword) {
      self.setKeywordStatus(self.keyword);
    }
  },
  debounceSetKeywordStatus: lodash.debounce(() => {
    self.setKeywordStatus(self.keyword);
  }, 600),
  setKeywordStatus: flow(function* (keyword) {
    self.isLoading = true;
    if (keyword && keyword.length >= 4) {
      const prefix = self?.publishDomainSelectStore?.selectedPublishDomain?.selectedPrefix?.prefix || "";
      if (!self.keywordIsValid(prefix, keyword)) {
        self.keywordStatus = false;
      } else {
        self.keywordStatus = yield self.isCodeURLUnique();
      }
    } else {
      self.keywordStatus = undefined;
    }
    self.handleHasErrors(!self.keywordStatus);
    self.isLoading = false;
  }),
  keywordIsValid(prefix, keyword) {
    // any lowercase/uppercase letter, digit, hyphen, comma, equals, at sign, forward slash, or underscore
    const pattern = new RegExp(self.shortUriPattern, 'i');
    const keywordIsURL = keyword && pattern.test(keyword);
    return keywordIsURL;
  },
  isCodeURLUnique: flow(function* () {
    if (!self?.publishDomainSelectStore?.selectedPublishDomain?.domain || !self.keyword) {
      return false;
    }
    self.isLoading = true;
    const uri = self.keyword;

    const available = yield getEnv(self).codeManager.isURLAvailable({
      "domain": self?.publishDomainSelectStore?.selectedPublishDomain?.domain,
      "prefix": self?.publishDomainSelectStore?.selectedPublishDomain?.selectedPrefix?.prefix || "",
      uri
    });
    self.handleHasErrors(!available);
    self.isLoading = false;
    return available;
  }),
  fetchAndHydrateDefaultSetting: flow(function* () {
    if (self?.defaultSettingFeatureFlag) {
      try {
        const query = buildDefaultSettingQuery({
          groupSelectStore: self.groupSelectStore,
          codeTypeSelectStore: self.codeTypeSelectStore,
          dimensionSelectViewState: self.dimensionSelectViewState
        });

        yield fetchAndHydrateDefaultSettingUtil({
          componentState: self,
          query,
          hasExtendedDataModal: true,
          selectExtendedDataSelection: self.selectExtendedDataSelection,
          designerConfig: {
            isDesignerTemplateExperience: false,
            isClone: false,
            hydrateDimensions: false
          }
        });
      } catch (e) {
        console.error(`${getType(self).name} Error: Failed to hydrateDefaultSetting`, e);
      }
    }
  }),

  getDimensions() {
    if (lodash.isEmpty(JSON.parse(self.selectedDimensions))) return undefined
    return JSON.parse(self.selectedDimensions)
  },
  validateDimension() {
    if (!self.dimensionSelectViewState?.dimensionsEnabled) return true
    let dimensions = self.getSelectedDimensions()
    const dimensionUuids = self.dimensionSelectViewState?.dimensionSelectStates.map(selectState => selectState?.selectStateUuid)
    dimensionUuids.forEach(uuid => {
      if (!dimensions.hasOwnProperty(uuid)) {
        self.dimensionSelectViewState?.dimensionSelectStates?.find(selectState => selectState?.selectStateUuid == uuid).setDefaults()
      }
    })
    dimensions = self.getSelectedDimensions()
    return dimensionUuids.every(uuid => dimensions.hasOwnProperty(uuid));
  },
  getSelectedDimensions() {
    return JSON.parse(self.selectedDimensions)
  },
  selectGroup(obj) {
    updateInfoAfterSelectGroup(self, obj)
    if (!self.protocolDefaultsEnabled) {
      const domain = self.publishDomainSelectStore?.selectedPublishDomain;
      if (domain?.defaultProtocol) {
        self.setSelectedProtocol(domain.defaultProtocol);
      }
    }
    if(obj){
      self.fetchAndHydrateDefaultSetting()
    }
  },
  selectTag(arr) {
    self.selectedTags = arr.flatMap((tag) => tag.uuid)
    // const root = getRoot(self)
    // console.log(obj.flatMap( (row)=> [resolveIdentifier(TagModel, root, row.value.uuid)]))
    // self.selectedTags = obj
    // .flatMap( (row)=> [resolveIdentifier(TagModel, root, row.value.uuid)])
  },
  clearExperienceData() {
    // Clear Data
    self.experienceData = '{}';
    self.secondaryExperienceData = '{}';
  },
  selectExperience(obj,behavior=null) {
    //TODO: check if its changed, and form-data exists, to alert the User if they want to change

    if (self.experienceSelected) {
      self.clearExperienceData()
    }

    // Check the behaviors supported by the new experience
    const hasOnlyStatic = obj.behavior?.length === 1 && obj.behavior.includes(codeBehavior.STATIC);
    const hasDynamic = obj.behavior?.includes(codeBehavior.DYNAMIC);

    // If experience has only static behavior, check if tenant feature is enabled
    if (hasOnlyStatic && !self.isStaticBehaviorEnabled) {
      new Notification()
        .setType("info")
        .setTitle("Static feature disabled")
        .setMessage("STATIC behavior is not enabled for the tenant. Cannot select this experience.")
        .send();

      // Block the change - return false to prevent selection
      return false;
    }

    self.experienceSelected = obj;

    // Determine behavior based on what the experience supports
    if (hasOnlyStatic) {
      // Only static behavior (and tenant feature is enabled if we reach here)
      self.behavior = codeBehavior.STATIC;
      self.isExperienceDynamic = false;
    } else if (hasDynamic) {
      // Has dynamic (either only dynamic or both dynamic and static) → assign dynamic as true
      self.behavior = codeBehavior.DYNAMIC;
      self.isExperienceDynamic = true;
    }

    if (behavior) { // if behavior is passed. An example is when cloning code
      // Check if trying to clone with STATIC behavior when feature is disabled
      if (behavior === codeBehavior.STATIC && !self.isStaticBehaviorEnabled) {
        new Notification()
          .setType("info")
          .setTitle("Static feature disabled")
          .setMessage("STATIC behavior is not enabled for the tenant. Cannot clone this code with static behavior.")
          .send();

        // Block the cloning - return false to prevent selection
        return false;
      }

      self.behavior = behavior;
      self.isExperienceDynamic = behavior === codeBehavior.DYNAMIC;
    }
    if (self.protocolDefaultsEnabled && self.protocolDefaults && Object.keys(self.protocolDefaults).length > 0) {
      const codeTypeName = obj?.name?.toLowerCase();
      const protocol = self.protocolDefaults[codeTypeName] || self.protocolDefaults.default || protocolConstants.HTTP;
      self.setSelectedProtocol(protocol);
    }

    self.fetchAndHydrateDefaultSetting();

    // Return true to allow the selection
    return true;
  },
  selectSecondaryExperience(obj){
    self.secondaryExperienceSelected = obj;
  },
  setVCardFetchDownloadLink(fn) {
    self.vCardDownloadLink = fn
  },
  selectPublishDomain(obj) {
    self.selectedDefaultUrl = obj.name
    if (!self.protocolDefaultsEnabled && obj?.defaultProtocol) {
      self.setSelectedProtocol(obj.defaultProtocol);
    }
    if (self.keyword) {
      self.setKeywordStatus(self.keyword);
    }
  },
  selectPageTemplate(obj) {
    self.selectedPage = obj
  },
  searchGroup(txt) {
    console.log(txt)
  },
  updateExperienceData(data) {
    if (self.isCodeTypeSocialMedia()) {
      self.updateFormData(data);
    }
    if (data.hasOwnProperty("enableWalletPass") && data?.enableWalletPass) {
      data = {
        ...data,
        ...walletExperienceDataBuilder(self.experienceSelected?.uuid, data),
      };
    }
    if (data.hasOwnProperty("passType")) {
      // For Mobile Wallet Pass experience
      ["eventTicket", "coupon", "storeCard", "generic"].forEach((t) => {
        if (data.hasOwnProperty(t) && data[t]) {
          data[t] = {
            ...data[t],
            "showMobileWalletCode": data[t].barcodeType === "None" || data[t].showMobileWalletCode
          };
        }
      });
    }
    self.experienceData = JSON.stringify(data)
  },
  updateSecondaryExperienceData(data) {
    if (self.secondaryExperienceSelected?.name === codeTypes.SocialMedia) {
      self.updateFormData(data);
    }
    if (data.hasOwnProperty("enableWalletPass") && data?.enableWalletPass) {
      data = {
        ...data,
        ...walletExperienceDataBuilder(self.secondaryExperienceSelected?.uuid, data),
      };
    }
    if (data.hasOwnProperty("passType")) {
      // For Mobile Wallet Pass experience
      ["eventTicket", "coupon", "storeCard", "generic"].forEach((t) => {
        if (data.hasOwnProperty(t) && data[t]) {
          data[t] = {
            ...data[t],
            "showMobileWalletCode": data[t].barcodeType === "None" || data[t].showMobileWalletCode
          };
        }
      });
    }
    self.secondaryExperienceData = JSON.stringify(data)
    console.log(self.toJSON())
  },
  addVCardDownLoadLinkData(data) {
    self.vCardDownLoadLinkData = JSON.stringify(data)
  },
  updateExperienceUISchema(uiSchema="{}") {
    self.experienceUISchema = uiSchema;
  },
  updateSecondaryExperienceUISchema(uiSchema="{}") {
    self.secondaryExperienceUISchema = uiSchema;
  },
  updateExperienceUISchemaOnData(data) {
    let uiSchema = JSON.parse(self.experienceUISchema);
    if (data.hasOwnProperty("passType")) {
      // For Mobile Wallet Pass experience
      ["eventTicket", "coupon", "storeCard", "generic"].forEach((t) => {
        if (uiSchema.hasOwnProperty(t) && data[t]) {
          uiSchema[t] = {
            ...uiSchema[t],
            "showMobileWalletCode": {
              "ui:readonly": data[t].barcodeType === "None"
            }
          };
        }
      });
    }

    if(self.experienceSelected.uuid === codeTypes.GS1) {
      uiSchema = self.updateGs1UISchema(data)
    }

    self.updateExperienceUISchema(JSON.stringify(uiSchema));
  },
  updateGs1UISchema(data) {
    if (lodash.get(data, 'keyQualifiers.mode') === "MAIN") {
      const experienceData = JSON.parse(self.experienceData);
      const keyQualifiersToOmit = [
        'serialNumber',
        'variant',
        'expirationDate',
        'dataAttributes'
      ];

      lodash.update(experienceData, 'keyQualifiers', keyQualifiers =>
        lodash.omit(keyQualifiers, keyQualifiersToOmit)
      );

      self.experienceData = JSON.stringify(experienceData);

      return {
        keyQualifiers: {
          mode: {
            "ui:widget": "toggleButtonGroup",
            "ui:options": { "label": false }
          },
          ...lodash.fromPairs(keyQualifiersToOmit.map(key => [key, { "ui:widget": "hidden" }]))
        }
      };
    }
    return {};
  },
  updateSecondaryExperienceUISchemaOnData(data) {
    let uiSchema = JSON.parse(self.secondaryExperienceUISchema);
    if (data.hasOwnProperty("passType")) {
      // For Mobile Wallet Pass experience
      ["eventTicket", "coupon", "storeCard", "generic"].forEach((t) => {
        if (uiSchema.hasOwnProperty(t) && data[t]) {
          uiSchema[t] = {
            ...uiSchema[t],
            "showMobileWalletCode": {
              "ui:readonly": data[t].barcodeType === "None"
            }
          };
        }
      });
    }
    self.updateSecondaryExperienceUISchema(JSON.stringify(uiSchema));
  },
  updatePreviousPlatform(platform) {
    self.previousPlatform = platform
  },
  updateFormData(data) {
    const platformDefaults = {
      "Facebook": ["Follow", "Share"],
      "Instagram": "Follow",
      "YouTube": ["Watch", "Subscribe"],
      "LinkedIn": "Connect",
      "Pinterest": "Follow",
      "Reddit": ["Follow", "Join"],
      "Snapchat": "Follow",
      "Telegram": "Follow",
      "TikTok": ["Follow", "Share"],
      "Tumblr": "Follow",
      "X (Twitter)": ["Follow", "Tweet"],
      "Line": "Add"
    };

    if (data.platform !== self.previousPlatform) {
      self.updatePreviousPlatform(data.platform);
      if (platformDefaults.hasOwnProperty(data.platform)) {
        data.username = "";
      }
    }

    if (Array.isArray(platformDefaults[data.platform])) {
      if (platformDefaults[data.platform].includes(data.action)) {
        data.action = data.action;
      } else {
        data.action = platformDefaults[data.platform][0];
      }
    } else {
      if (data.action === platformDefaults[data.platform]) {
        data.action = data.action;
      } else {
        data.action = platformDefaults[data.platform];
      }
    }
  },
  toggleTrue(str) {
    self[str] = true
  },
  toggleIsExperienceDynamicBoolean(){
    self.isExperienceDynamic = !self.isExperienceDynamic
  },
  toggleIsExperienceDynamic() {
    self.toggleIsExperienceDynamicBoolean()
    const staticFeatureEnabled = self.isStaticBehaviorEnabled; // tenant flag
    if (self.experienceSelected?.behavior?.length > 1) {
      if (self.isExperienceDynamic) {
        self.behavior = codeBehavior.DYNAMIC
      } else {
        if(!staticFeatureEnabled) {
          new Notification()
                  .setType("info")
                  .setTitle("Static feature disabled")
                  .setMessage("STATIC behavior is not enabled for your tenant.")
                  .send();
          setTimeout(() => {
            self.toggleIsExperienceDynamicBoolean()
          }, 500)
          return
        }
        self.behavior = codeBehavior.STATIC
      }
    } else {
      if (self.experienceSelected?.behavior[0] === codeBehavior.DYNAMIC) {
        new Notification()
            .setType("info")
            .setTitle("Dynamic Code")
            .setMessage("Only DYNAMIC code is available for this experience.")
            .send();
      }
      if (self.experienceSelected?.behavior[0] === codeBehavior.STATIC) {
        if (!staticFeatureEnabled) {
          new Notification()
            .setType("info")
            .setTitle("Static feature disabled")
            .setMessage("STATIC behavior is not enabled for your tenant.")
            .send();
        } else {
          new Notification()
            .setType("info")
            .setTitle("Static Code")
            .setMessage("Only STATIC code is available for this experience.")
            .send();
        }
      }
      setTimeout(() => {
        self.toggleIsExperienceDynamicBoolean()
      }, 500)
    }
  },
  toggleIsAutoGeneratedUrl(configs) {
    if (configs.qr_auto_generation) {
      self.isAutoGeneratedUrl = !self.isAutoGeneratedUrl
    } else {
      self.isAutoGeneratedUrl = !self.isAutoGeneratedUrl
      new Notification()
        .setType("info")
        .setTitle("Authorization")
        .setMessage("Only auto generation is available to you")
        .send();
      setTimeout(() => {
        self.toggleTrue("isAutoGeneratedUrl")
      }, 500)
    }
    self.setKeyword("");
    self.handleHasErrors(false);
  },

  transformErrors(errors) {
    // TODO: move this into schema, see example of gs1type for reference
    return errors.map(error => {
      if (error.name === "pattern") {
        switch (error.property) {
          case ".text": error.message = "Invalid SMS message body"
            break;
          case ".url": error.message = "Must be a valid url"
            break;
        }
      }
      return error;
    });
  },
  validateSchema(schema, formData) {
    const ajv = createCustomAjvValidator().ajv;
    const validate = ajv.compile(schema)
    let valid = validate(formData)
    if (!valid) {
      self.liveValidateSchema = true
    }
    return valid
  },
  addExperienceFieldAndMediaFileUuid(uuid,experienceFieldName) {
    if(uuid && experienceFieldName){ //make sure undefined is not being set to the list
      self.mediaFiles = {...self.mediaFiles, [experienceFieldName]:uuid}
    }

  },
  setMediaFileToSchema(file, experienceFieldName) {
    self.addExperienceFieldAndMediaFileUuid(file?.uuid, experienceFieldName)
    if (file?.uuid) {
      if (["eps"].includes(file.fileFormat) && file.liveLinks?.png) {
        self.updateExperienceData({
          [experienceFieldName]: file.liveLinks?.png,
          "fileType": getFileCategory("png")
        })
      } else if (file.liveLinks?.original) {
        self.updateExperienceData({
          [experienceFieldName]: file.liveLinks?.original,
          "fileType": getFileCategory(file.fileFormat)
        })
      } else {
        new Notification()
          .setType("info")
          .setTitle(`File: ${file.label}`)
          .setMessage("Is processing. Can take 30 seconds")
          .send();
        self.updateExperienceData({[experienceFieldName]: ""})
      }
    } else if (file?.name) { // reason for no uuid is that there is no location yet.
      self.updateExperienceData({[experienceFieldName]: ""})
    }
  },
  setFileForS3Upload(file,singleUseFile){
    self.fileForS3Upload = file
    self.mediaFile.updateMediaFile({name: file.name,singleUseFile: singleUseFile})
  },
  selectMediaFile(mediaFile){
    self.mediaFile.updateMediaFile(mediaFile)
  },
  handleFilePicState(image){
    self.viewFilePicState = ViewFilePicState.create({fileImageUrl: image})
  },
  validateForm() {
    let isExperienceFormValid = self.validateSchema(JSON.parse(self.experienceSelected.schema), JSON.parse(self.experienceData))
    if(self.experienceSelected.uuid === 'gs1' && JSON.parse(self.experienceData)?.keyQualifiers?.mode !== "SUB"){
      isExperienceFormValid = isExperienceFormValid && self.validateSchema(JSON.parse(self.secondaryExperienceSelected.schema), JSON.parse(self.secondaryExperienceData));
    }
    if (self?.getExtendedDataList()?.length) {
      const isExtenedFormValid = self.extendedDataSettingModalState?.validateForm();
      return isExperienceFormValid && isExtenedFormValid;
    }
    return isExperienceFormValid;
  },
  checkDefaultSetting() {
    if (!(self.experienceSelected.name === "url" || self.experienceSelected.name === "gs1")) {
      self.utmModalState?.setNoneOpen();
      self.addUtmToCode();
      self.parameterSetModalState?.setNoneOpen();
      self.addParameterSetToCode();
    }
  },
  updateSingleUseMediaFile: flow(function* (mediaFile,experienceFieldName) {
    self.experienceData = '{}' // <== removes value from the widget so when item is selected updated values will render
    yield self.mediaFileSelectState?.updateSingleUseFile(mediaFile)
    self.mediaFileSelectState?.select({...mediaFile},experienceFieldName)
  }),
  updateMediaFile: flow(function* (mediaFile,experienceFieldName) {
    self.experienceData = '{}' // <== removes value from the widget so when item is selected updated values will render
    self.updateExperienceData({[experienceFieldName]: mediaFile.liveLinks?.original})
    self.mediaFileSelectState?.store.updateMediaFile(mediaFile)
    yield self.mediaFileSelectState?.handleAddAndSelectNewMediaFile(mediaFile.uuid,experienceFieldName) // select the updated mediaFile
  }),
  setCancelUploads(value) {
    self.cancelUploads = value
  },
  waitForMediaFileUpload: flow(function* (mediaFileUUID, experienceFieldName) {
    self.isLoading = true;
    self.cancelUploads = false;

    const fileReady = yield new Promise((resolve) => {
      let count = 0;
      const interval = setInterval(async () => {
        count++;
        const response = await getEnv(self).mediaFileManager?.checkMediaFileStatus(mediaFileUUID);

        if (self.cancelUploads === true) {
          clearInterval(interval);
          resolve(false);
        } else if (response?.status === "UPLOADED") {
          clearInterval(interval);
          resolve(true); // Resolve as "true" if uploaded

          if (response?.singleUseFile === true) {
            self.updateSingleUseMediaFile(response, experienceFieldName);
          } else {
            self.updateMediaFile(response, experienceFieldName);
          }
        } else if (response?.status === "FAILED") {
          self.mediaFileSelectState.setSelectedToUndefined()
          self.mediaFileSelectState.store.removeFileByUuid(mediaFileUUID)
          self.updateExperienceData({[experienceFieldName]: ""})
          new Notification()
            .setType("error")
            .setMessage("Failed to upload file")
            .send(); // TODO: make this localised String
          clearInterval(interval);
          resolve(false);
        } else if (count > 100) {
          self.mediaFileSelectState.setSelectedToUndefined()
          self.mediaFileSelectState.store.removeFileByUuid(mediaFileUUID)
          self.updateExperienceData({[experienceFieldName]: ""})
          new Notification()
            .setType("warning")
            .setMessage("File is taking longer than expected to upload. Try a different file or come back later")
            .send(); // TODO: make this localised String
          clearInterval(interval);
          resolve(false); // Resolve as "false" after 100 attempts or cancel
        }
      }, 1000);
    });

    self.isLoading = false;
    return fileReady;
  }),

  createMediaFileFromWidget: flow(function* f(experienceFieldName) {

    try {
      if (self.mediaFile?.singleUseFile) {
        // Add timestamp to file name so you don't add the same file twice in the list
        self.mediaFile.updateMediaFile({name: addTimestampToFileName(self.mediaFile?.name)});
      }

      const file = self.fileForS3Upload
      const fileFormat = getFileFormat(file)
      self.mediaFile.updateMediaFile({"fileFormat": fileFormat})
      const response = yield getEnv(self).mediaFileManager.createNewLocationUploadFile(self);

      if (response instanceof Error) {
        throw response;
      }
      if (!response?.uuid) {
        throw new Error("Missing uuid in response: " + JSON.stringify(response));
      }
      if (response.singleUseFile === true) {
        self.mediaFileSelectState.store.addFileToList(response);
        self.mediaFileSelectState.select(response, experienceFieldName);
      } else {
         self.mediaFileSelectState.store.addFileToList(response);
        yield self.mediaFileSelectState?.handleAddAndSelectNewMediaFile(response.uuid);
      }
      self.waitForMediaFileUpload(response.uuid, experienceFieldName);

      return response;
    } catch (error) {
      console.error("Failed to create mediaFile", error.message || error);
      const message =  error.message || error
      new Notification()
        .setType("error")
        .setTitle(``)
        .setMessage(message)
        .send();
      return error;
    }
  }),

  saveNew: flow(function* f() {
    // TODO: validate Input
    if (!self.validateDimension()) {
      // TODO: Make this generic for all validations
      new Notification()
        .setType("error")
        .setMessage("select atleast one dimensions each")
        .send(); // TODO: make this localised String
      self.isLoading = false
      return
    }
    if (self.experienceSelected.name === "appLink") {
      const experienceDataObj = JSON.parse(self.experienceData);

      try {
        ["fallback", "playStore", "appStore"].forEach((key) => {
          validateAndFormatUrl({
            url: experienceDataObj[key],
            targetObj: experienceDataObj,
            key
          });
        });

        self.experienceData = JSON.stringify(experienceDataObj);
      } catch (e) {
        new Notification()
          .setType("error")
          .setMessage(e.message)
          .send();
        self.isLoading = false;
        return;
      }
    }
    if (
      self.experienceSelected.name === "url" ||
      self.experienceSelected.name === "socialMedia" ||
      self.experienceSelected.name === "gs1"
    ) {
      const experienceDataObj = JSON.parse(self.experienceData);
      const secondaryExperienceDataObj = JSON.parse(self.secondaryExperienceData);

      try {
        validateAndFormatUrl({
          url: experienceDataObj.url,
          targetObj: experienceDataObj,
          key: "url"
        });
        self.experienceData = JSON.stringify(experienceDataObj);

        if (self.experienceSelected.name === "gs1") {
          validateAndFormatUrl({
            url: secondaryExperienceDataObj.url,
            targetObj: secondaryExperienceDataObj,
            key: "url"
          });
          self.secondaryExperienceData = JSON.stringify(secondaryExperienceDataObj);
        }
      } catch (e) {
        new Notification()
          .setType("error")
          .setMessage(e.message)
          .send();
        self.isLoading = false;
        return;
      }
    }
    const validSchema = self.validateForm()
    self.checkDefaultSetting()
    if (validSchema) {
      // Creating image config
      const extendConfig = self?.designerModalState.designerViewStore.extendConfig
      if ( extendConfig && extendConfig !== "{}") {
        const imageConfigManager = getEnv(self).imageConfigManager
        const newImageConfigUuid = yield imageConfigManager.addImageConfigForCode(self.designerModalState, extendConfig)
        self.imageUuid = newImageConfigUuid
      }
      self.isLoading = true
      const manager = getEnv(self).codeManager
      const resp = yield manager.save(self);
      self.formHasErrors && new Notification()
        .setType("error")
        .setMessage(`${self.name} Code failed creating: ${self.errorMessage}`)
        .send();
      !self.formHasErrors && new Notification()
        .setType("success")
        .setMessage(`${self.name} Code created`)
        .send()
      && self.notifyShortUrl(resp.data.shortUrl.domain + "/" + resp.data.shortUrl.uri)
      self.handleHasErrors(false)
      self.isLoading = false
      return resp
    } else {
      return null
    }
  }),
  addRulesToCode() {
    const rules = self.ruleSetModalState.createRuleSetState.grabRuleSetForCode()
    if (rules?.length > 0) {
      self.rules = self.ruleSetModalState.noRuleOpen ? "[]" : rules
      return self.rules
    } else {
      return null
    }
  }
  ,
  addUtmToCode() {
    // if (self.utmModalState.noUtmOpen || !self.utmModalState.utm.doesUtmHaveValue()) {
    //   self.utm = Utm.create({uuid: "noUtm"})
    // } else {
    //   self.utm = self.utmModalState.utm.grabUtmForCode()
    // }
    addUtmToCode(self)
  },
  addParameterSetToCode() {
    if (!self.parameterSetModalState.noParameterSetOpen) {
      self.parameters.setParameters("codeParamType",self.parameterSetModalState.codeParamsTypeSelectState.selectedCodeParameters.map((param) => param.uuid))
      self.parameters.setParameters("scanParamType",self.parameterSetModalState.scanParamsTypeSelectState.selectedScanParameters.map((param) => param.uuid))
      self.parameters.setParameters("countParamType",self.parameterSetModalState.countParamsTypeSelectState.selectedCountParameters.map((param) => param.uuid))
      self.parameters.setParameters("premiumParamType",self.parameterSetModalState.premiumParamsTypeSelectState.selectedPremiumParameters.map((param) => param.uuid))
      self.parameters.setParameters("customParamType",self.parameterSetModalState.customParamsTypeSelectState.selectedCustomParameters.map((param) => param.uuid))
      self.parameters.setParameters("gs1ParamType",self.parameterSetModalState.gs1ParamsTypeSelectState.selectedGS1Parameters.map((param) => param.uuid))
      self.parameters.setParameters("edParamType", self.parameterSetModalState.parameterExtendedDataSelectStore.selectedExtendedData.map((param) => param.uuid))
    } else if (self.parameterSetModalState.noParameterSetOpen || !self.parameterSetModalState.doesParamsHaveValues()) {
      self.parameters.clearParams()
    }
  },
  updateImageUuid(uuid) {
    self.imageUuid = uuid
  },
  afterCreate() {
    //self.selectExperience(self.codeTypeSelectStore?.selected)
  }
})).views((self) => ({

  configButtonColor(bool) {
    return bool ? "secondary" : "primary"
  },
  configClassName(bool) {
    return bool ? "btn-outline-secondary mr-1 mb-1" : "btn-outline-primary mr-1 mb-1"
  },
  isCodeTypeSocialMedia() {
    return self.experienceSelected?.name === codeTypes.SocialMedia
  },
  shouldRenderAutoGenerateToggle() {
    return self.experienceSelected?.behavior?.includes(codeBehavior.DYNAMIC) && self.isExperienceDynamic
  },
  shouldRenderDomainSection() {
    return self.experienceSelected?.behavior?.includes(codeBehavior.DYNAMIC) && self.isExperienceDynamic
  },
  // advanced configs
  shouldRenderExtendedData() {
    return true; // todo: default for all?
  },
  shouldRenderUTMConfig() {
    return (self.experienceSelected.name === codeTypes.URL || self.experienceSelected.name === codeTypes.GS1)
        && self.isExperienceDynamic
  },
  shouldRenderParametersConfig() {
    return (self.experienceSelected.name === codeTypes.URL || self.experienceSelected.name === codeTypes.GS1)
        && self.isExperienceDynamic
  },
  shouldRenderRulesConfig() {
    return (self.experienceSelected.name === codeTypes.URL || self.experienceSelected.name === codeTypes.GS1)
        && self.isExperienceDynamic
  },
  shouldRenderStatusConfig() {
    return self.experienceSelected?.behavior?.includes(codeBehavior.DYNAMIC) && self.isExperienceDynamic
  },
  shouldRenderSecondTabInPreview(){
    return  self.isExperienceDynamic
  },
  experienceDataView() {
    return JSON.parse(self.experienceData)
  },
  secondaryExperienceDataView() {
    return JSON.parse(self.secondaryExperienceData)
  },
  experienceExtendedDataView() {
    return self.extendedDataSettingModalState?.formData;
  },
  experienceSchemaJson() {
    //TODO: this method is being called everytime code name or url is changed, needs refactoring
    let {schema} = self.experienceSelected || {}
    console.log("Schema is :" + schema);
    schema = schema || JSON.stringify(defaultSchemaJson);
    return JSON.parse(schema);
  },
  secondaryExperienceSchemaJson() {
    let {schema} = self.secondaryExperienceSelected || {}
    console.log("Schema is :" + schema);
    schema = schema || JSON.stringify(defaultSchemaJson);
    return JSON.parse(schema);
  },
  experienceUISchemaView() {
    let {uiSchema} = self.experienceSelected || {};
    self.updateExperienceUISchema(JSON.stringify({
      ...JSON.parse(uiSchema || '{}'),
      ...JSON.parse(self.experienceUISchema)
    }));
    return JSON.parse(self.experienceUISchema);
  },
  secondaryExperienceUISchemaView() {
    const secondaryExperience = self.secondaryExperienceSelected || {};
    let {uiSchema} = secondaryExperience;
    self.updateSecondaryExperienceUISchema(JSON.stringify({
      ...JSON.parse(uiSchema || '{}'),
      ...JSON.parse(self.secondaryExperienceUISchema)
    }));
    return JSON.parse(self.secondaryExperienceUISchema);
  },
  parametersHaveValue() {
    return self.parameters.codeParamType.length > 0
        || self.parameters.scanParamType.length > 0
        || self.parameters.countParamType.length > 0
        || self.parameters.premiumParamType.length > 0
        || self.parameters.customParamType.length > 0
        || self.parameters.gs1ParamType.length > 0
        || self.parameters.edParamType.length > 0
  },
  hasExtendedData() {
    return self.getExtendedDataList()?.length && self.extendedDataSettingModalState?.showDropdown;
  },
  getExtendedDataList() {
    return self.extendedDataList?.filter(n => n);
  },
  getExtendedDataRequest() {
    return self.hasExtendedData() ? {
      "items": self?.getExtendedDataList()?.map((item) => item.uuid),
      "data": self?.experienceExtendedDataView()
    } : {};
  },
  isStatusNotDefault() {
    return (self.status != "PUBLISHED" || self.statusViewState.scheduleStatusChange);
  },
  isSaveDisabled() {
    return self.isAutoGeneratedUrl ? (self.isLoading) : (self.isLoading || !self.keywordStatus);
  }
}))

export default CreateCode;
